import { CloudProjectFileTransfer } from "./CloudProjectFileTransfer";
import { CloudProgress } from "./CloudProgress";
import { ProcessType } from "./ProcessType"; // Ensure you import this from your project
import { EmptyResponse } from "./CloudResponse";
import { ScreenshotProvider } from "../../shared/sync/dataTypes/ScreenshotProvider";


class CloudProcess {
    private id: string;
    type: ProcessType;
    error?: EmptyResponse;
    
    private _shouldCancel: boolean = false;

    private completed: boolean = false;
    private filesTransfer: CloudProjectFileTransfer[] = [];

    constructor(id: string, type: ProcessType) {
        this.id = id;
        this.type = type;
    }

    get failed(): boolean {
        return !!this.error;
    }

    get canProceed(): boolean {
        return !this._shouldCancel && !this.failed;
    }

    shouldCancel(): boolean {
        return this._shouldCancel;
    }

    private cancelTasks(): boolean {
        this._shouldCancel = true;
        let listOfAlreadyCancelled = this.filesTransfer.map((fileTransfer) => {
            const task = fileTransfer.task;
            if (task && task.cancel) {
                return task.cancel();
            }
        });
        return listOfAlreadyCancelled.every((a) => !a);
    }

    async cancel(): Promise<void> {
        if (this.completed) {
            console.log(`sync: canceling a completed process of ${this.type.toString()} process: ${this.id}`);
            return;
        }

        this._shouldCancel = true;
        const typeString = this.type.toString();
        const currentId = this.id;
        console.log(`sync: canceling ${typeString} process: ${currentId}`);

        if (this.filesTransfer.length === 0) return;

        return new Promise<void>((resolve) => {
            this.notifyOnCompletion(() => {
                console.log(`sync: did cancel ${typeString} process: ${currentId}`);
                resolve();
            });

            let areAlreadyCancelled = this.cancelTasks();
            if (areAlreadyCancelled) {
                resolve();
            }
        });
    }

    didFailed(error: EmptyResponse): void {
        this.error = error;
    }

    private completionBlocks: ((success: boolean) => void)[] = [];

    notifyOnCompletion(comp: (success: boolean) => void): void {
        this.completionBlocks.push(comp);
    }

    didComplete(success: boolean): void {
        this.completed = true;
        console.log(`sync: did complete ${this.type.toString()} process: ${this.id}, success: ${success}`);

        this.completionBlocks.forEach((callback) => {
            callback(success);
        });

        this.completionBlocks = [];
    }

    async runFileTransfers(files: CloudProjectFileTransfer[], progress?: CloudProgress): Promise<EmptyResponse> {
        console.log(`sync: run process: ${this.id}`);
        this.filesTransfer = files;
        const screenshot = files.find((file) => file.relativePath.endsWith(ScreenshotProvider.screenshotFilename()));
        const highRes = files.find((file) => file.relativePath.endsWith(ScreenshotProvider.highResFilename()));

        const links = files.filter((file) => file.isHardLink);
        const other = files.filter((file) => file !== highRes && file !== screenshot && !file.isHardLink);

        let filesToTransfer = [screenshot, highRes].filter(Boolean).concat(other)

        let isUploadingOnlyTask = files.every (f=>f.type == ProcessType.Upload)
        let retryAccount = isUploadingOnlyTask ? 0 : 2; // no need to retry upload the firebase sdk does it for us 
        const maxConcurrent = Infinity
        // const transfers: EmptyResponse[] = await this.asyncResponseActions(
        //     filesToTransfer,
        //     maxConcurrent,
        //     (transfer) => transfer.run(retryAccount, this, progress)
        // );

        const transfers: EmptyResponse[] = await Promise.all(filesToTransfer.map(t => t.run(retryAccount, this, progress)));;
        // let failedTransfer = transfers.find((t) => !t.succeed)
        let failedTransfer = transfers.length > 0 && transfers.every((t) => !t.succeed)
        if (failedTransfer) {
            this.didComplete(false);
            let transfer = transfers.find((t) => !t.succeed)
            return transfer
            // return failedTransfer
        }

        const res: EmptyResponse[] = await Promise.all(links.map(t => t.run(retryAccount, this, progress)));;

        // let failedLinkTransfer = res.find((t) => !t.succeed)
        let failedLinkTransfer = res.length > 0 && res.every((t) => !t.succeed)
        if (failedLinkTransfer) {
            this.didComplete(false);
            let transfer = res.find((t) => !t.succeed)
            return transfer
            // return failedLinkTransfer
        }

        this.didComplete(true);
        return EmptyResponse.success();
    }
}

export { CloudProcess };